import { Selection, TextSelection } from "@tiptap/pm/state";

import {
  closestCollapsedBlock,
  collapsedPositionsNear,
  collapsedRunInfo,
  collapsedRunSelection,
} from "./blockCollapse";
import { isBlockCollapsible, isCollapsed } from "./utils";

import type { EditorView } from "@tiptap/pm/view";

const ARROW_EDGE: Record<string, "up" | "down" | "left" | "right" | undefined> = {
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
};

/**
 * Arrow-key handling around collapsed blocks. A collapsed block renders no
 * contentDOM, so ProseMirror would otherwise skip over it. Returns true (and
 * dispatches) when it handled the key, false to let normal navigation run.
 *
 * A run of consecutive collapsed blocks renders as one merged bar, so it behaves as a
 * single navigation stop: a highlight always lands on the run's start (the visible bar)
 * and stepping off it skips past the whole run.
 *
 * (A) When a collapsed block is highlighted (node-selected), move past its run,
 *     re-highlighting the next run's start if that block is also collapsed.
 * (B) When the caret sits at the edge of its text block and the next block in
 *     document order is collapsed, land a highlight on its run start instead of crossing.
 *     Works at any depth — the collapsed block can be a top-level sibling or nested
 *     inside a callout/blockquote/list.
 * (C) When `extend` (Shift-arrow), keep the anchor and move only the head across the whole
 *     run. The browser's default vertical extension breaks when a run ends in a hidden leaf
 *     (graph/chart): with no layout to measure, it collapses the head to the document start.
 */
export function arrowToCollapsed(view: EditorView, key: string, extend = false): boolean {
  const { state } = view;
  const { selection, doc } = state;
  const forward = key === "ArrowRight" || key === "ArrowDown";
  const backward = key === "ArrowLeft" || key === "ArrowUp";

  if (!forward && !backward) {
    return false;
  }

  const dir = forward ? 1 : -1;

  // (A) A highlighted run moves as one stop — but only when not extending: a Shift-arrow
  // keeps its anchor and just moves the head (handled by the walk below).
  if (!extend) {
    const runInfo = collapsedRunInfo(view, selection);
    if (runInfo) {
      const side = dir > 0 ? runInfo.runEnd : runInfo.first;
      const near = Selection.near(doc.resolve(side), dir);
      const intoCollapsed = closestCollapsedBlock(near.$from);
      const next = intoCollapsed ? collapsedRunSelection(view, intoCollapsed.pos) : near;

      view.dispatch(state.tr.setSelection(next).scrollIntoView());
      return true;
    }
  }

  if (!(selection instanceof TextSelection) || (!extend && !selection.empty)) {
    return false;
  }

  const edge = ARROW_EDGE[key];
  if (!edge || !view.endOfTextblock(edge)) {
    return false;
  }

  const $pivot = extend ? selection.$head : selection.$from;
  const { depth: startDepth } = $pivot;

  for (let depth = startDepth; depth >= 1; depth--) {
    const parent = $pivot.node(depth - 1);
    const index = $pivot.index(depth - 1) + dir;

    if (index < 0 || index >= parent.childCount) {
      continue;
    }

    const targetNode = parent.child(index);
    if (!isCollapsed(targetNode) || !isBlockCollapsible(targetNode)) {
      return false;
    }

    const targetPos = $pivot.posAtIndex(index, depth - 1);

    if (extend) {
      // (C) Move the head across the whole run, keeping the anchor.
      const run = collapsedPositionsNear(view, targetPos);
      const runLast = run[run.length - 1];
      const head = dir > 0 ? runLast + (doc.nodeAt(runLast)?.nodeSize ?? 0) : run[0];

      view.dispatch(
        state.tr.setSelection(TextSelection.create(doc, selection.anchor, head)).scrollIntoView(),
      );
      return true;
    }

    // (B) Land a highlight on the run's start instead of crossing into hidden content.
    view.dispatch(state.tr.setSelection(collapsedRunSelection(view, targetPos)).scrollIntoView());
    return true;
  }

  return false;
}
